import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useNotifications } from '../context/NotificationContext';
import { cn, getInitials } from '../lib/utils';
import { listAPI, workspaceAPI } from '../lib/api';
import UserStatusSelector from './discussion/UserStatusSelector';
import {
    LayoutDashboard,
    ListTodo,
    Target,
    Clock,
    Users,
    Settings,
    LogOut,
    Plus,
    ChevronDown,
    ChevronRight,
    Folder,
    User,
    BarChart3,
    List,
    Calendar,
    FolderOpen,
    Eye,
    Briefcase,
    UserCheck,
    FolderKanban,
    Building2,
    Building,
    Handshake,
    Bot,
    Activity,
    Loader2,
    MessageSquare,
    Hash,
    AtSign,
    Check,
    SquarePen,
    Video,
    Mail,
    Trash2,
    X,
    ClipboardList,
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export const Sidebar = ({ isMobileMenuOpen = false, closeMobileMenu }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { workspaces, currentWorkspace, spaces, currentSpace, selectWorkspace, selectSpace, currentUserRole, isReadOnly, fetchWorkspaces, storageStats } = useWorkspace();
    const [adminExpanded, setAdminExpanded] = useState(false);
    const [taskManagerExpanded, setTaskManagerExpanded] = useState(true);
    const [projectManagerExpanded, setProjectManagerExpanded] = useState(true);
    const [discussionExpanded, setDiscussionExpanded] = useState(true);
    const [pmAreaExpanded, setPmAreaExpanded] = useState(true);
    const [companyGroupExpanded, setCompanyGroupExpanded] = useState(true);
    const [expandedSpaces, setExpandedSpaces] = useState({});
    const [spaceLists, setSpaceLists] = useState({});
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const { totalUnreadChannels, totalUnreadDMs } = useNotifications();
    const [showStatusPopup, setShowStatusPopup] = useState(false);
    const [userStatus, setUserStatus] = useState('online');
    const [isCompanyOwner, setIsCompanyOwner] = useState(false);
    const [isCompanyMember, setIsCompanyMember] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [fileEnabled, setFileEnabled] = useState(false);
    const [enabledMenus, setEnabledMenus] = useState(null);

    // Check Nextcloud File feature availability for the user's company.
    useEffect(() => {
        if (!isCompanyMember) {
            setFileEnabled(false);
            return;
        }
        (async () => {
            try {
                const API_BASE = process.env.REACT_APP_BACKEND_URL;
                const authToken = localStorage.getItem('token');
                if (!authToken) return;
                const res = await fetch(`${API_BASE}/api/nextcloud/context`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setFileEnabled(Boolean(data?.enabled));
                }
            } catch (err) {
                setFileEnabled(false);
            }
        })();
    }, [isCompanyMember]);

    // Check if user is in a company
    useEffect(() => {
        const checkCompany = async () => {
            try {
                const API_BASE = process.env.REACT_APP_BACKEND_URL;
                const authToken = localStorage.getItem('token');
                if (!authToken) return;
                const res = await fetch(`${API_BASE}/api/companies/my`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data) {
                        setIsCompanyMember(true);
                        setCompanyName(data.name || '');
                        if (data.my_role === 'owner') {
                            setIsCompanyOwner(true);
                        }
                        // Store enabled_menus (null means all menus enabled)
                        setEnabledMenus(data.enabled_menus || null);
                    }
                }
            } catch (err) {
                // Not in a company
            }
        };
        if (user) checkCompany();
    }, [user]);
        
    // (total unread now comes from NotificationContext)
    const loadListsForSpace = async (spaceId) => {
        if (spaceLists[spaceId]) return;
        try {
            const res = await listAPI.getBySpace(spaceId);
            setSpaceLists(prev => ({ ...prev, [spaceId]: res.data }));
        } catch (error) {
            console.error('Failed to load lists:', error);
        }
    };

    const toggleSpaceExpanded = (spaceId) => {
        const isExpanding = !expandedSpaces[spaceId];
        setExpandedSpaces(prev => ({ ...prev, [spaceId]: isExpanding }));
        if (isExpanding) {
            loadListsForSpace(spaceId);
        }
    };

    useEffect(() => {
        initPage();
    }, []);

    const initPage = async () => {
        if (!currentWorkspace) {
            await fetchWorkspaces();
        }
    };

    const handleCreate = async (newName, newDesc, newColor) => {
        try {
            const response = await workspaceAPI.create({ name: newName, description: newDesc, color: newColor });
            toast.success('Workspace created');
            await fetchWorkspaces();
            selectWorkspace(response.data);
            setIsCreateModalOpen(false);
            navigate('/admin/settings?tab=workspace')
        } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to create workspace';
            toast.error(msg);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Navigate and close mobile menu
    const handleNavClick = useCallback((path) => {
        navigate(path);
        if (closeMobileMenu) closeMobileMenu();
    }, [navigate, closeMobileMenu]);

    const isAdminOrOwner = currentUserRole === 'owner' || currentUserRole === 'admin';
    const isCrmUser = currentUserRole === 'owner' || currentUserRole === 'admin' || currentUserRole === 'sales';
    const isPmUser = currentUserRole === 'owner' || currentUserRole === 'admin' || currentUserRole === 'project_manager';
    const isClient = currentUserRole === 'client';

    // Helper: check if a menu item is enabled based on company settings
    // enabledMenus is null/undefined → all items enabled (backward compat)
    const isMenuEnabled = (section, itemKey) => {
        if (!enabledMenus) return true; // null = all enabled
        return (enabledMenus[section] || []).includes(itemKey);
    };

    // Company Group section items (only for company members)
    const allCompanyGroupItems = [
        { path: '/guest-messages', icon: MessageSquare, label: 'Guest Message', key: 'guest_messages' },
        ...(isCompanyOwner ? [{ path: '/company-management', icon: Settings, label: 'Company Setting', key: 'company' }] : []),
    ];
    const companyGroupItems = allCompanyGroupItems.filter(item => isMenuEnabled('company_group', item.key));

    // Main section items
    const allMainNavItems = [
        { path: '/', icon: LayoutDashboard, label: 'Dashboard', key: 'dashboard' },
        { path: '/calendar', icon: Calendar, label: 'Calendar', key: 'calendar' },
        ...(isCompanyMember ? [{ path: '/mail/inbox', icon: Mail, label: 'Mail', key: 'mail' }] : []),
        ...(isCompanyMember && fileEnabled ? [{ path: '/files', icon: FolderOpen, label: 'File', key: 'file' }] : []),
    ];
    const mainNavItems = allMainNavItems.filter(item => isMenuEnabled('main', item.key));

    // Task Manager section items
    const allTaskManagerItems = [
        { path: '/my-tasks', icon: User, label: 'My Task', key: 'my_task' },
        { path: '/review-by-me', icon: Eye, label: 'Review by Me', key: 'review_by_me' },
        { path: '/assigned-by-me', icon: UserCheck, label: 'Assign By Me', key: 'assign_by_me' },
        { path: '/tasks', icon: ListTodo, label: 'Task', key: 'tasks' },
    ];
    const taskManagerItems = allTaskManagerItems.filter(item => isMenuEnabled('task_manager', item.key));

    // CRM Area section items
    const allCrmAreaItems = [
        { path: '/crm/partnerships', icon: Handshake, label: 'Contact', key: 'partnerships' },
        { path: '/crm/leads', icon: UserCheck, label: 'Lead', key: 'leads' },
        { path: '/crm/clients', icon: Building2, label: 'Client', key: 'clients' },
        { path: '/crm/projects', icon: Briefcase, label: 'Project', key: 'projects' },
    ];
    const crmAreaItems = allCrmAreaItems.filter(item => isMenuEnabled('crm_area', item.key));

    // Admin section items
    const allAdminNavItems = [
        { path: '/team', icon: Users, label: 'Team', key: 'team' },
        { path: '/ai-agents', icon: Bot, label: 'AI Agents', key: 'ai_agents' },
        { path: '/admin/tracking-member', icon: Activity, label: 'Tracking Member', key: 'tracking_member' },
        { path: '/admin/dashboard', icon: BarChart3, label: 'Dashboard Admin', key: 'dashboard_admin' },
        { path: '/admin/deleted-tasks', icon: Trash2, label: 'Deleted Tasks', key: 'deleted_tasks' },
        { path: '/admin/settings', icon: Settings, label: 'Settings', key: 'settings' },
    ];
    const adminNavItems = allAdminNavItems.filter(item => isMenuEnabled('admin_area', item.key));

    // Discussion section items
    const allDiscussionItems = [
        { path: '/discussion', icon: Hash, label: 'Channel', key: 'channel' },
        { path: '/direct-messages', icon: AtSign, label: 'Direct Message', key: 'direct_message' },
    ];
    const discussionItems = allDiscussionItems.filter(item => isMenuEnabled('discussion', item.key));

    // Project Manager Area section items
    // Daily Standup punya dua gate independen: toggle per workspace + menu toggle per company
    const allPmAreaItems = [
        ...(currentWorkspace?.daily_standup_enabled
            ? [{ path: '/daily-standup', icon: ClipboardList, label: 'Daily Standup', key: 'daily_standup' }]
            : []),
    ];
    const pmAreaItems = allPmAreaItems.filter(item => isMenuEnabled('project_manager', item.key));

    // Reusable nav item component (full label, works for both desktop and mobile drawer)
    const renderNavItem = (item, section = '') => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        const showChannelBadge = item.path === '/discussion' && totalUnreadChannels > 0;
        const showDMBadge = item.path === '/direct-messages' && totalUnreadDMs > 0;
        const showBadge = showChannelBadge || showDMBadge;
        const badgeCount = showChannelBadge ? totalUnreadChannels : (showDMBadge ? totalUnreadDMs : 0);

        return (
            <Link
                key={item.path}
                to={item.path}
                onClick={closeMobileMenu}
                className={cn('nav-item', isActive && 'active')}
                data-testid={`nav-${section}${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                title={item.label}
            >
                <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                <span className="sidebar-label-text truncate flex-1">{item.label}</span>
                {showBadge && badgeCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                        {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                )}
            </Link>
        );
    };

    const sidebarContent = (
        <>
            {/* Logo & Workspace Selector - Fixed at top */}
            <div className="p-4 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                        <img
                            src="/assets/logo-icon.png"
                            alt="TaskGlory"
                            className="w-8 h-8 flex-shrink-0"
                        />
                        <span className="font-heading font-semibold text-white sidebar-label-text">TaskGlory</span>
                    </div>
                    {/* Close button - mobile only */}
                    {closeMobileMenu && (
                        <button
                            onClick={closeMobileMenu}
                            className="lg:hidden p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                            aria-label="Close menu"
                        >
                            <X className="w-5 h-5 text-white/60" strokeWidth={1.5} />
                        </button>
                    )}
                </div>
                
                {/* Workspace Dropdown */}
                <div className="flex items-center gap-1">
                <DropdownMenu>
                    <DropdownMenuTrigger className="flex-1" data-testid="workspace-selector">
                        <div className="flex items-center justify-between p-2 rounded-lg bg-white/10 hover:bg-white/15 transition-colors cursor-pointer">
                            <div className="flex items-center gap-2 min-w-0">
                                <div 
                                    className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                                    style={{ backgroundColor: currentWorkspace?.color || '#2F6F63' }}
                                >
                                    {currentWorkspace?.name?.[0]?.toUpperCase() || 'W'}
                                </div>
                                <span className="text-sm font-medium truncate sidebar-label-text">
                                    {currentWorkspace?.name || 'Select Workspace'}
                                </span>
                            </div>
                            <ChevronDown className="w-4 h-4 text-white/60 sidebar-chevron" strokeWidth={1.5} />
                        </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56" align="start">
                        {workspaces.map((ws) => (
                            <DropdownMenuItem
                                key={ws.id}
                                onClick={() => selectWorkspace(ws)}
                                className={cn(currentWorkspace?.id === ws.id && 'bg-brand-accent-soft')}
                                data-testid={`workspace-item-${ws.id}`}
                            >
                                <div 
                                    className="w-5 h-5 rounded mr-2 flex items-center justify-center text-xs font-bold text-white"
                                    style={{ backgroundColor: ws.color }}
                                >
                                    {ws.name[0].toUpperCase()}
                                </div>
                                {ws.name}
                            </DropdownMenuItem>
                        ))}
                        {!isCompanyMember && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setIsCreateModalOpen(true)} data-testid="create-workspace-btn">
                                    <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    Create Workspace
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
                </div>
            </div>

            {/* Scrollable Navigation Area */}
            <div className="flex-1 overflow-y-auto py-4 custom-scrollbar">
                <nav className="space-y-1 px-2">
                    {/* COMPANY GROUP Section - Only for company members */}
                    {isCompanyMember && companyGroupItems.length > 0 && (
                    <div className="mb-4">
                        <Collapsible open={companyGroupExpanded} onOpenChange={setCompanyGroupExpanded}>
                            <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between px-3 py-1 cursor-pointer hover:bg-white/5 rounded sidebar-section-header">
                                    <span className="text-xs font-semibold uppercase tracking-wider text-white/40 sidebar-label-text truncate">{companyName || 'Company'}</span>
                                    <ChevronRight className={cn(
                                        'w-4 h-4 text-white/40 transition-transform sidebar-chevron',
                                        companyGroupExpanded && 'rotate-90'
                                    )} strokeWidth={1.5} />
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <div className="mt-2 space-y-1">
                                    {companyGroupItems.map((item) => renderNavItem(item, 'company-'))}
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
                    )}

                    {/* MAIN Section */}
                    {mainNavItems.length > 0 && (
                    <div className="mb-4">
                        <span className="px-3 text-xs font-semibold uppercase tracking-wider text-white/40 sidebar-label-text sidebar-section-header">Main</span>
                        <div className="mt-2 space-y-1">
                            {mainNavItems.map((item) => renderNavItem(item, 'main-'))}
                        </div>
                    </div>
                    )}

                    {/* TASK MANAGER Section */}
                    {taskManagerItems.length > 0 && (
                    <div className="mb-4">
                        <Collapsible open={taskManagerExpanded} onOpenChange={setTaskManagerExpanded}>
                            <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between px-3 py-1 cursor-pointer hover:bg-white/5 rounded sidebar-section-header">
                                    <span className="text-xs font-semibold uppercase tracking-wider text-white/40 sidebar-label-text">Task Manager</span>
                                    <ChevronRight className={cn(
                                        'w-4 h-4 text-white/40 transition-transform sidebar-chevron',
                                        taskManagerExpanded && 'rotate-90'
                                    )} strokeWidth={1.5} />
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <div className="mt-2 space-y-1">
                                    {taskManagerItems.map((item) => renderNavItem(item, 'task-'))}
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
                    )}

                    {/* DISCUSSION Section */}
                    {discussionItems.length > 0 && (
                    <div className="mb-4">
                        <Collapsible open={discussionExpanded} onOpenChange={setDiscussionExpanded}>
                            <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between px-3 py-1 cursor-pointer hover:bg-white/5 rounded sidebar-section-header">
                                    <span className="text-xs font-semibold uppercase tracking-wider text-white/40 sidebar-label-text">Discussion</span>
                                    <ChevronRight className={cn(
                                        'w-4 h-4 text-white/40 transition-transform sidebar-chevron',
                                        discussionExpanded && 'rotate-90'
                                    )} strokeWidth={1.5} />
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <div className="mt-2 space-y-1">
                                    {discussionItems.map((item) => renderNavItem(item, 'discussion-'))}
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
                    )}

                    {/* PROJECT MANAGER AREA Section — Only for owner/admin/project_manager */}
                    {isPmUser && pmAreaItems.length > 0 && (
                    <div className="mb-4">
                        <Collapsible open={pmAreaExpanded} onOpenChange={setPmAreaExpanded}>
                            <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between px-3 py-1 cursor-pointer hover:bg-white/5 rounded sidebar-section-header">
                                    <span className="text-xs font-semibold uppercase tracking-wider text-white/40 sidebar-label-text">Project Manager Area</span>
                                    <ChevronRight className={cn(
                                        'w-4 h-4 text-white/40 transition-transform sidebar-chevron',
                                        pmAreaExpanded && 'rotate-90'
                                    )} strokeWidth={1.5} />
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <div className="mt-2 space-y-1">
                                    {pmAreaItems.map((item) => renderNavItem(item, 'pm-'))}
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
                    )}

                    {/* CRM AREA Section - Only for owner/admin/sales */}
                    {isCrmUser && crmAreaItems.length > 0 && (
                        <div className="mb-4">
                            <Collapsible open={projectManagerExpanded} onOpenChange={setProjectManagerExpanded}>
                                <CollapsibleTrigger className="w-full">
                                    <div className="flex items-center justify-between px-3 py-1 cursor-pointer hover:bg-white/5 rounded sidebar-section-header">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-white/40 sidebar-label-text">CRM Area</span>
                                        <ChevronRight className={cn(
                                            'w-4 h-4 text-white/40 transition-transform sidebar-chevron',
                                            projectManagerExpanded && 'rotate-90'
                                        )} strokeWidth={1.5} />
                                    </div>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <div className="mt-2 space-y-1">
                                        {crmAreaItems.map((item) => renderNavItem(item, 'crm-'))}
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        </div>
                    )}

                    {/* ADMIN AREA Section - Only for admin/owner */}
                    {isAdminOrOwner && adminNavItems.length > 0 && (
                        <div className="mb-4">
                            <Collapsible open={adminExpanded} onOpenChange={setAdminExpanded} defaultOpen={true}>
                                <CollapsibleTrigger className="w-full">
                                    <div className="flex items-center justify-between px-3 py-1 cursor-pointer hover:bg-white/5 rounded sidebar-section-header">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-white/40 sidebar-label-text">Admin Area</span>
                                        <ChevronRight className={cn(
                                            'w-4 h-4 text-white/40 transition-transform sidebar-chevron',
                                            adminExpanded && 'rotate-90'
                                        )} strokeWidth={1.5} />
                                    </div>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <div className="mt-2 space-y-1">
                                        {adminNavItems.map((item) => renderNavItem(item, 'admin-'))}
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        </div>
                    )}
                </nav>

                {/* SPACES Section */}
                {currentWorkspace && spaces.length > 0 && (
                    <div className="mt-2 px-2">
                        <div className="flex items-center justify-between px-3 mb-2">
                            <span className="text-xs font-semibold uppercase tracking-wider text-white/40 sidebar-label-text">Spaces</span>
                            {!isReadOnly && (
                                <button 
                                    onClick={() => navigate('/settings?tab=spaces')}
                                    className="text-white/50 hover:text-white transition-colors"
                                    data-testid="add-space-btn"
                                >
                                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            )}
                        </div>
                        <div className="space-y-1">
                            {spaces.map((space) => (
                                <Collapsible key={space.id} open={expandedSpaces[space.id]} onOpenChange={() => toggleSpaceExpanded(space.id)}>
                                    <CollapsibleTrigger asChild>
                                        <div
                                            className={cn('space-item', currentSpace?.id === space.id && 'active')}
                                            onClick={() => selectSpace(space)}
                                            data-testid={`space-item-${space.id}`}
                                        >
                                            <div 
                                                className="space-color-dot"
                                                style={{ backgroundColor: space.color }}
                                            />
                                            <span className="truncate flex-1 sidebar-label-text">
                                                {space.prefix && <span className="text-white/50 mr-1">{space.prefix}</span>}
                                                {space.name}
                                            </span>
                                            <ChevronRight className={cn(
                                                'w-4 h-4 opacity-50 transition-transform sidebar-chevron',
                                                expandedSpaces[space.id] && 'rotate-90'
                                            )} strokeWidth={1.5} />
                                        </div>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="ml-6 mt-1 space-y-0.5">
                                            <Link
                                                to={`/space/${space.id}/dashboard`}
                                                onClick={closeMobileMenu}
                                                className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 hover:text-white hover:bg-white/10 rounded-md cursor-pointer transition-colors"
                                            >
                                                <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                <span>Dashboard Client</span>
                                            </Link>
                                            
                                            {spaceLists[space.id]?.length > 0 ? (
                                                spaceLists[space.id].map(list => (
                                                    <Link
                                                        key={list.id}
                                                        to={`/tasks?space=${space.id}&list=${list.id}`}
                                                        onClick={closeMobileMenu}
                                                        className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 hover:text-white hover:bg-white/10 rounded-md cursor-pointer transition-colors"
                                                        data-testid={`list-item-${list.id}`}
                                                    >
                                                        <List className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                        <span className="truncate">{list.name}</span>
                                                    </Link>
                                                ))
                                            ) : (
                                                <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/40">
                                                    <List className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                    <span>No lists yet</span>
                                                </div>
                                            )}
                                            
                                            <Link
                                                to={`/space/${space.id}/files`}
                                                onClick={closeMobileMenu}
                                                className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 hover:text-white hover:bg-white/10 rounded-md cursor-pointer transition-colors"
                                            >
                                                <FolderOpen className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                <span>File Manager</span>
                                            </Link>

                                            {(currentWorkspace?.owner_id === user?.id || space.space_admin_ids?.includes(user?.id)) && (
                                                <Link
                                                    to={`/space/${space.id}/settings`}
                                                    onClick={closeMobileMenu}
                                                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 hover:text-white hover:bg-white/10 rounded-md cursor-pointer transition-colors"
                                                >
                                                    <Settings className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                    <span>Setting</span>
                                                </Link>
                                            )}
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* User Menu with Status - Fixed at bottom */}
            <div className="p-4 border-t border-white/10 flex-shrink-0">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex-1">
                        <UserStatusSelector className="text-white hover:bg-white/10" />
                    </div>
                    
                    {/* Settings & Logout Dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-white/70 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                            >
                                <Settings className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                            {isCompanyOwner && (
                                <>
                                    <DropdownMenuItem onClick={() => { navigate('/company-management'); closeMobileMenu?.(); }}>
                                        <Building2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                        Company Manager
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => { navigate('/zoom-settings'); closeMobileMenu?.(); }}>
                                        <Video className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                        Zoom Settings
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => { navigate('/zimbra-settings'); closeMobileMenu?.(); }}>
                                        <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                        Zimbra Mail Settings
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                </>
                            )}
                            <DropdownMenuItem onClick={() => { navigate('/admin/settings?tab=profile'); closeMobileMenu?.(); }}>
                                <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Settings
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleLogout} className="text-brand-error">
                                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Sign out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
                
            {/* Storage Quota — di bawah user profile */}
            {storageStats && (
                <div className="px-4 pb-3 flex-shrink-0">
                    <div className="flex justify-between text-xs text-white/40 mb-1">
                        <span>Storage</span>
                        <span>{storageStats.used_gb.toFixed(1)} / {storageStats.quota_gb} GB</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1">
                        <div
                            className={`h-1 rounded-full transition-all ${
                                storageStats.percent >= 95 ? 'bg-red-500' :
                                storageStats.percent >= 80 ? 'bg-yellow-400' : 'bg-green-400'
                            }`}
                            style={{ width: `${Math.min(storageStats.percent, 100)}%` }}
                        />
                    </div>
                </div>
            )}

            <CreateWorkspaceModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreate={handleCreate}
            />
        </>
    );

    return (
        <aside className="sidebar flex flex-col h-screen" data-testid="sidebar">
            {sidebarContent}
        </aside>
    );
};

const CreateWorkspaceModal = ({ isOpen, onClose, onCreate }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [color, setColor] = useState('#2F6F63');
    const [loading, setLoading] = useState(false);

    const colorOptions = ['#2F6F63', '#1F4D45', '#7ED957', '#F5C26B', '#F26B6B', '#6EC1E4'];

    const handleSubmit = async () => {
        if (!name.trim()) {
            toast.error('Workspace name is required');
            return;
        }
        setLoading(true);
        await onCreate(name, description, color);
        setLoading(false);
        setName('');
        setDescription('');
        setColor('#2F6F63');
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent data-testid="create-workspace-modal">
                <DialogHeader>
                    <DialogTitle>Create Workspace</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                    <div>
                        <Label>Name</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="My Workspace"
                            className="mt-1"
                            data-testid="new-workspace-name-input"
                        />
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional description"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label>Color</Label>
                        <div className="flex gap-2 mt-2">
                            {colorOptions.map(c => (
                                <button
                                    key={c}
                                    onClick={() => setColor(c)}
                                    className={cn(
                                        'w-8 h-8 rounded-full transition-all',
                                        color === c && 'ring-2 ring-offset-2 ring-brand-primary'
                                    )}
                                    style={{ backgroundColor: c }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button 
                        onClick={handleSubmit}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        disabled={loading}
                        data-testid="create-workspace-submit-btn"
                    >
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Create
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};


export default Sidebar;